import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeStatus, DeviceType, Theme, DeviceFamily, BuildingId, FeatureId } from 'shared/models';
import { ThemeMenu } from '../themeMenu/ThemeMenu';
import { Dictionary, values } from 'lodash';
import { formatBytes } from 'util/formatBytes';
import { Permission } from 'shared/permissions';
import { useSession } from 'hooks';

require('./ThemeList.css');

const themeStatusTextKey = {
    [ThemeStatus.Idle]: 'THEMES.STATUS.IDLE',
    [ThemeStatus.Queued]: 'THEMES.STATUS.QUEUED',
    [ThemeStatus.InProgress]: 'THEMES.STATUS.IN_PROGRESS',
    [ThemeStatus.Error]: 'THEMES.STATUS.ERROR',
};

const themeStatusIcon = {
    [ThemeStatus.Idle]: 'grey minus icon',
    [ThemeStatus.Queued]: 'grey wait icon',
    [ThemeStatus.InProgress]: 'black spinner loading icon',
    [ThemeStatus.Error]: 'bordered inverted red warning icon publishing-error-icon',
};

const opacity25 = { opacity: .25 };
const opacity50 = { opacity: .50 };
const opacity100 = { opacity: 1 };
const verticalAlignMiddle = { verticalAlign: 'middle' };
const textAlignCenter: React.CSSProperties = { textAlign: 'center' };
const textAlignRight: React.CSSProperties = { textAlign: 'right' };

const ThemeStatusCell = ({ status, canReset, onClickReset }: { status?: string; canReset: boolean; onClickReset: () => void }) => {
    const { t } = useTranslation();
    if (!status) return null;
    return (
        <span style={ status === ThemeStatus.Idle ? opacity25 : opacity100 }>
            <i className={ themeStatusIcon[status] }/>
            <span style={ verticalAlignMiddle }>
                { t(themeStatusTextKey[status]) }
            </span>
            { canReset &&
                <button
                    type="button"
                    onClick={ onClickReset }
                    className="ui tiny basic compact button theme-list-reset-button"
                >
                    { t('COMMAND.RESET') }
                </button>
            }
        </span>
    );
};

const PublishedStatusCell = ({ published, lastSaved, url }: { published: Date | null; lastSaved?: Date; url?: string | null }) => {
    const { t } = useTranslation();
    return (
        published && lastSaved && url
            ? <i className={ published >= lastSaved ? 'green checkmark icon' : 'bordered inverted yellow checkmark icon' } title={ t(published >= lastSaved ? 'THEMES.PUBLISHED' : 'THEMES.PUBLISHED_STALE') }/>
            : <i className="grey minus icon" style={ opacity25 } title={ t('THEMES.NOT_PUBLISHED') }/>
    );
};

const PreviewCell = ({ published, url }: { published: Date | null; url?: string | null }) => {
    const { t } = useTranslation();
    const previewUrl = url?.replace(/\.[^/.]+$/, '.html');
    
    return (
        published && previewUrl
            ? <a
                className="ui tiny basic compact button theme-list-preview-button"
                href={ `${previewUrl}?${Math.random().toString(36).substring(2, 5)}&preview` }
                target='_blank'
                rel="noopener noreferrer">
                { t('COMMAND.PREVIEW') }
            </a>
            : <></>
    );
};

type ItemParams = {
    theme: Theme;
    buildingId: BuildingId | null;
    isLockedOut: boolean;
    deviceTypes: Dictionary<DeviceType>;
    onClick: () => void;
    onClickEdit: () => void;
    onClickPublish: () => void;
    onClickExport: () => void;
    onClickDownload: () => void;
    onClickReset: () => void;
    onClickSchedule: () => void;
    onClickCopy: () => void;
    onClickDelete: () => void;
};

const ThemeListItem = (params: ItemParams) => {
    const { t } = useTranslation();
    const { hasPermission, hasFeature } = useSession();
    const { theme, onClick, onClickReset, deviceTypes, isLockedOut, buildingId } = params;
    const { id, displayName, width, height, status, url, published, lastSaved, uploaded, requirements, size } = theme;

    const { Matisse, TTG, pi, deviceTypeId } = requirements;
    const deviceType = (deviceTypes && deviceTypeId) ? deviceTypes[deviceTypeId!] : undefined;
    const deviceTypeNameByClass =
        TTG
            ? 'TTG'
            : Matisse
                ? 'Matisse'
                : t('DEVICE_TYPES.GENERIC');
    const deviceTypeName = deviceType ? deviceType.displayName : deviceTypeNameByClass;
    
    const canPublish = !isLockedOut && !uploaded && deviceType
        && deviceType?.deviceFamily !== DeviceFamily.Giotto &&
        deviceType?.deviceFamily !== DeviceFamily.Raffaello &&
        (status === ThemeStatus.Idle || status === ThemeStatus.Error)
    ;

    const showExport =
        deviceType?.deviceFamily === DeviceFamily.MatisseR5
        || deviceType?.deviceFamily === DeviceFamily.Giotto
        || deviceType?.deviceFamily === DeviceFamily.Raffaello
        || deviceType?.deviceFamily === DeviceFamily.MatisseFlexR2
    ;

    const canExport = !isLockedOut && !uploaded && deviceType && showExport
        && (status === ThemeStatus.Idle || status === ThemeStatus.Error)
        && hasPermission(buildingId, Permission.EditDevice)
        && hasFeature(FeatureId.ThemeExport);
    const canEdit = !isLockedOut && !uploaded && hasPermission(null, Permission.EditTheme);
    const canCopy = !isLockedOut && hasPermission(null, Permission.CreateTheme);
    const canDelete = !isLockedOut && hasPermission(buildingId, Permission.DeleteTheme);
    const canDownload = uploaded || TTG;
    const canSchedule = !isLockedOut && published;
    const canReset = status === ThemeStatus.InProgress || status === ThemeStatus.Error;

    const hidePreview = deviceType && deviceType.deviceFamily === DeviceFamily.Giotto;
    return (
        <tr>
            <td>
                { !canEdit
                    ? <span>{ displayName || id }</span>
                    : <span className="link" onClick={ onClick }>{ displayName || id }</span>
                }
            </td>
            <td>
                { deviceTypeName || <span style={ opacity25 }>{ t('NONE_PROVIDED') }</span> }
            </td>
            <td>
                { pi
                    ? <i className="sort icon" title={ t('THEMES.COLUMN.PI_TOOLTIP') }/>
                    : null
                }
            </td>
            <td className="text-align-center">
                { `${width} x ${height}` }
            </td>
            <td className="text-align-center">
                <PublishedStatusCell published={ published } lastSaved={ lastSaved } url={ url }/>
            </td>
            <td className="text-align-center">
                { !uploaded && !hidePreview && <PreviewCell published={ published } url={ url }/> }
            </td>
            <td className="text-align-right">
                { size && formatBytes(t, size) }
            </td>
            <td>
                { !uploaded && <ThemeStatusCell status={ status } canReset={ canReset } onClickReset={ onClickReset }/> }
            </td>
            <td className="text-align-right">
                <ThemeMenu
                    { ...params }
                    canEdit={ canEdit }
                    canDelete={ canDelete }
                    canPublish={ canPublish }
                    canSchedule={ canSchedule }
                    canExport={ canExport }
                    canCopy={ canCopy }
                    canDownload={ canDownload }
                />
            </td>
        </tr>
    );
};

const compareAlphabetically = (a: Theme, b: Theme) => {
    const aName = (a.displayName || a.id).toLowerCase();
    const bName = (b.displayName || b.id).toLowerCase();
    return aName < bName ? -1 : aName > bName ? 1 : 0;
};

export type Params = {
    themes: Dictionary<Theme> | null;
    isLockedOut: boolean;
    buildingId: BuildingId | null;
    deviceTypes: Dictionary<DeviceType>;
    onClick: (themeId: string) => () => void;
    onClickEdit: (themeId: string) => () => void;
    onClickPublish: (themeId: string) => () => void;
    onClickDownload: (url: string | null) => () => void;
    onClickReset: (themeId: string) => () => void;
    onClickSchedule: (themeId: string) => () => void;
    onClickExport: (themeId: string) => () => void;
    onClickCopy: (themeId: string) => () => void;
    onClickDelete: (themeId: string) => () => void;
};

export const ThemeList = ({ themes, isLockedOut, buildingId, deviceTypes, onClick, onClickEdit, onClickPublish, onClickDownload, onClickReset, onClickSchedule, onClickExport, onClickCopy, onClickDelete }: Params) => {
    const { t } = useTranslation();
    return (
        <table className="theme-list ui single line compact table" style={ { width: '960px' } }>
            <colgroup>
                <col style={ { width: '28%' } }/>
                <col style={ { width: '18%' } }/>
                <col style={ { width: '5%' } }/>
                <col style={ { width: '13%' } }/>
                <col style={ { width: '4%' } }/>
                <col style={ { width: '13%' } }/>
                <col style={ { width: '7%' } }/>
                <col style={ { width: '5%' } }/>
                <col style={ { width: '7%' } }/>
            </colgroup>
            <thead>
                <tr>
                    <th>{ t('THEMES.COLUMN.NAME') }</th>
                    <th>{ t('THEMES.COLUMN.DEVICE_TYPE') }</th>
                    <th>{ t('THEMES.COLUMN.PI') }</th>
                    <th style={ textAlignCenter }>{ t('THEMES.COLUMN.RESOLUTION') }</th>
                    <th style={ textAlignCenter }>{ t('THEMES.COLUMN.PUBLISHED') }</th>
                    <th></th>
                    <th style={ textAlignRight }>{ t('THEMES.COLUMN.SIZE') }</th>
                    <th>{ t('THEMES.COLUMN.STATUS') }</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {!themes &&
                    <tr><td colSpan={ 7 }>{ t('LOADING') }</td></tr>
                }
                {themes && !Object.keys(themes).length &&
                    <tr><td colSpan={ 7 } style={ opacity50 }>{ t('BUILDING.THEMES.NO_THEMES') }</td></tr>
                }
                {themes && values(themes).sort(compareAlphabetically).map(theme => {
                    const id = theme.id;
                    const url = theme.url;
                    return (
                        <ThemeListItem
                            key={ id }
                            theme={ theme }
                            deviceTypes={ deviceTypes }
                            buildingId={ buildingId }
                            isLockedOut={ isLockedOut }
                            onClick={ onClick(id) }
                            onClickEdit={ onClickEdit(id) }
                            onClickPublish={ onClickPublish(id) }
                            onClickExport={ onClickExport(id) }
                            onClickDownload={ onClickDownload(url) }
                            onClickReset={ onClickReset(id) }
                            onClickSchedule={ onClickSchedule(id) }
                            onClickCopy={ onClickCopy(id) }
                            onClickDelete={ onClickDelete(id) }
                        />
                    );
                })}
            </tbody>
        </table>
    );
};
